import React, { useState } from 'react';
import { MessageKind, refactorCalendarName } from '../utils/common';
import API from '../utils/const';
import { authFetch } from '../utils/session';
import { createToast } from './Toast';
import { Button, Modal } from 'react-bootstrap';
import LoadingOverlay from './LoadingOverlay';
import { useTranslation } from 'react-i18next';
import { SyncRule } from '../utils/rule';
import { IconPencil, IconInfoCircle } from '@tabler/icons-react';
import { Drawer, Flex, Group, Text, Stack, Tooltip, Box } from '@mantine/core';
import SyncRuleModal from './SyncRuleModal';
import { Account } from './AccountCard';
import CalendarAccessWarning from './CalendarAccessWarning';
import ReconnectAccountModal from './ReconnectAccountModal';
import {
  findCalendarByUuid,
  getReconnectTooltipKey,
  isCalendarInaccessible,
} from '../utils/calendarAccess';

const SyncRuleRow: React.FC<{ rule: SyncRule, accounts: Account[], onChange: () => void }> = ({ rule, accounts, onChange }) => {
  const { t } = useTranslation(['app']);

  const [clickedDelete, setClickedDelete] = useState(false);
  const [clickedUpdate, setClickedUpdate] = useState(false);
  const [clickedResync, setClickedResync] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deletionInProcess, _] = useState(sessionStorage.getItem(`rule-delete-${rule.uuid}`) != null)
  const [summary, setSummary] = useState(rule.summary);
  const [description, setDescription] = useState(rule.description);
  const [reconnectAccount, setReconnectAccount] = useState<Account | null>(null);

  const sourceCalendar = findCalendarByUuid(rule.source_uuid, accounts);
  const destinationCalendar = findCalendarByUuid(rule.destination_uuid, accounts);
  const sourceInaccessible = sourceCalendar != null && isCalendarInaccessible(sourceCalendar.calendar);
  const destinationInaccessible = destinationCalendar != null && isCalendarInaccessible(destinationCalendar.calendar);

  async function deleteRule() {
    try {
      setLoading(true)
      const response = await authFetch(
        `${API}/sync/${rule.uuid}`,
        {
          method: "DELETE",
        }
      )
      if (response.ok) {
        createToast("Your rule has been set for deletion. This can take a few minutes to complete.", MessageKind.Info)
        sessionStorage.setItem(`rule-delete-${rule.uuid}`, 'true')
        onChange();
      } else {
        const data = await response.json();
        if (data.detail) {
          createToast(data.detail, MessageKind.Error)
        } else {
          createToast("Error while deleting synchronization", MessageKind.Error)
        }
      }

    } finally {
      setLoading(false)
    }
  }

  function updateRule() {
    createToast("Your rule is being updated. This can take a few minutes to complete.", MessageKind.Info)
    onChange();
  }

  async function refreshRule() {
    try {
      setLoading(true)
      const response = await authFetch(
        `${API}/sync/${rule.uuid}/resync`,
        {
          method: "POST",
          headers: {
            'Content-Type': 'application/json',
          }
        }
      )
      if (response.ok) {
        createToast("Your rule is being refreshed, this can take several minutes", MessageKind.Info)
      } else {
        const data = await response.json();
        if (data.detail) {
          createToast(data.detail, MessageKind.Error)
        } else {
          createToast("Error while refreshing sync rule", MessageKind.Error)
        }
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="card">
      {loading && <LoadingOverlay />}
      <Stack gap={0} px={'16px'} py={'8px'}>
        <Flex w="100%" justify="space-between" direction={{ base: 'column', sm: 'row' }} gap={{ base: '8px', sm: 0 }}>
          <Group>
            {deletionInProcess &&
              <>
                <span className="badge bg-primary text-light my-sm-1 border" style={{ maxWidth: "94px" }}>Active</span>
                <div className='text-wrap'>[Rule set to be deleted]</div>
              </>
            }
            <Text>{t("dashboard.sync.valid.from")} <Text span className='fw-bold'>{refactorCalendarName(rule.source)}</Text> {t("dashboard.sync.valid.to")} <Text span className='fw-bold'>{refactorCalendarName(rule.destination)}</Text></Text>
          </Group>
          <Group gap={'8px'} w={{ base: '100%', sm: 'auto' }}>
            <button type="button" className="btn btn-outline-primary me-1" onClick={() => setClickedUpdate(true)}>
              <div className='d-flex align-items-center py-1'>
                <IconPencil size={16} />
              </div>
            </button>
            <button type="button" className="btn btn-outline-primary me-1" onClick={() => setClickedResync(true)}>
              <div className='d-flex align-items-center py-1'>
                <svg fill="currentColor" height="16" width="16" version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 383.748 383.748">
                  <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                  <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                  <g id="SVGRepo_iconCarrier"> <g>
                    <path d="M62.772,95.042C90.904,54.899,137.496,30,187.343,30c83.743,0,151.874,68.13,151.874,151.874h30 C369.217,81.588,287.629,0,187.343,0c-35.038,0-69.061,9.989-98.391,28.888C70.368,40.862,54.245,56.032,41.221,73.593 L2.081,34.641v113.365h113.91L62.772,95.042z"></path>
                    <path d="M381.667,235.742h-113.91l53.219,52.965c-28.132,40.142-74.724,65.042-124.571,65.042 c-83.744,0-151.874-68.13-151.874-151.874h-30c0,100.286,81.588,181.874,181.874,181.874c35.038,0,69.062-9.989,98.391-28.888 c18.584-11.975,34.707-27.145,47.731-44.706l39.139,38.952V235.742z"></path>
                  </g>
                  </g>
                </svg>
              </div>
            </button>
            <button type="button" className="btn btn-outline-danger" onClick={() => setClickedDelete(true)}>
              <div className='d-flex align-items-center py-1'>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-trash" viewBox="0 0 16 16">
                  <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z" />
                  <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z" />
                </svg>
              </div>
            </button>
          </Group>
        </Flex>
        {rule.source_deleted &&
          <Box mt={4}>
            <Tooltip
              multiline
              maw={300}
              label="This calendar was not found during the last account refresh and has been marked as deleted. As a result, this sync rule is no longer active."
              withArrow
              position="top"
            >
              <Group gap={4} style={{ cursor: 'help', display: 'inline-flex', width: 'fit-content' }}>
                <IconInfoCircle size={16} color="red" />
                <Text c="red" size="sm" fw={500}>Source calendar deleted</Text>
              </Group>
            </Tooltip>
          </Box>
        }
        {sourceInaccessible &&
          <Box mt={4}>
            <CalendarAccessWarning
              label={t('dashboard.calendar_inaccessible.source_label')}
              tooltip={t(getReconnectTooltipKey(sourceCalendar!.account.provider))}
              reconnectLabel={t('dashboard.calendar_inaccessible.reconnect_link')}
              onReconnect={() => setReconnectAccount(sourceCalendar!.account)}
            />
          </Box>
        }
        {destinationInaccessible &&
          <Box mt={4}>
            <CalendarAccessWarning
              label={t('dashboard.calendar_inaccessible.destination_label')}
              tooltip={t(getReconnectTooltipKey(destinationCalendar!.account.provider))}
              reconnectLabel={t('dashboard.calendar_inaccessible.reconnect_link')}
              onReconnect={() => setReconnectAccount(destinationCalendar!.account)}
            />
          </Box>
        }
      </Stack>
      <Modal
        show={clickedResync === true}
        onHide={() => setClickedResync(false)}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            {t("dashboard.sync.valid.are-you-sure")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className=''>
          <p>
            {t("dashboard.sync.valid.resync-info")}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => { setClickedResync(false); refreshRule() }} variant='light'>Resync</Button>
          <Button onClick={() => setClickedResync(false)} variant='secondary'>Cancel</Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={clickedDelete === true}
        onHide={() => setClickedDelete(false)}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            {t("dashboard.sync.valid.are-you-sure")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className=''>
          <p>
            {t("dashboard.sync.valid.delete-info")}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => { setClickedDelete(false); deleteRule() }} variant='danger'>Delete</Button>
          <Button onClick={() => setClickedDelete(false)} variant='secondary'>Cancel</Button>
        </Modal.Footer>
      </Modal>
      <Drawer
        opened={clickedUpdate} onClose={() => setClickedUpdate(false)}
        withCloseButton={true}
        transitionProps={{ duration: 500 }}
        size='xl'
      >
        <SyncRuleModal
          close={() => setClickedUpdate(false)}
          rule={rule}
          accounts={accounts}
          successCallback={() => { setClickedUpdate(false); updateRule() }}
        />
      </Drawer>
      {reconnectAccount != null &&
        <ReconnectAccountModal
          opened={true}
          onClose={() => setReconnectAccount(null)}
          accountName={reconnectAccount.key}
          provider={reconnectAccount.provider}
        />
      }
    </div >
  );
};

export default SyncRuleRow;
